import { useState, useCallback, useRef } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { getCroppedImg } from './canvasUtils'
import { Button } from '../Button'
import './ImageEditor.css'

export interface ImageEditorInfo {
  croppedArea: Area | undefined,
  rotation: number | undefined
  zoom: number | undefined
  crop: { x: number, y: number } | undefined
}

interface ImageEditorProps {
  imageUrl: string
  aspect: number | undefined
  editedInfo: ImageEditorInfo | undefined
  onSave: (editedCanvas: HTMLCanvasElement, editedInfo: ImageEditorInfo) => void
  onCancel: () => void
}

type EditMode = 'crop' | 'rotate' | 'none'

export function ImageEditor({ imageUrl, aspect = 1, editedInfo, onSave, onCancel }: ImageEditorProps) {

  const [crop, setCrop] = useState(editedInfo?.crop || { x: 0, y: 0 })
  const [zoom, setZoom] = useState(editedInfo?.zoom || 1)
  const [rotation, setRotation] = useState(editedInfo?.rotation || 0)
  const [mode, setMode] = useState<EditMode>('crop')
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | undefined>(editedInfo?.croppedArea)
  const [isProcessing, setIsProcessing] = useState(false)
  const initZoomRef = useRef<number>(1)

  // 处理裁剪区域变化
  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  // 保存编辑
  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return

    setIsProcessing(true)
    try {
      // await new Promise(resolve => setTimeout(resolve, 2100))
      const croppedCanvas = await getCroppedImg(imageUrl, croppedAreaPixels, rotation)
      if (!croppedCanvas) {
        throw new Error('Failed to get cropped image')
      }
      let editedInfo: ImageEditorInfo = {
        croppedArea: croppedAreaPixels,
        rotation: rotation,
        zoom: zoom,
        crop: crop
      }
      onSave(croppedCanvas, editedInfo)
    } catch (error) {
      console.error('Failed to crop image:', error)
    } finally {
      setIsProcessing(false)
    }
  }, [croppedAreaPixels, imageUrl, rotation, onSave])

  // 重置编辑
  const handleReset = useCallback(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(initZoomRef.current)
    setRotation(0)
    setCroppedAreaPixels(undefined)
  }, [])


  const _renderSlidView = (title: string, desc: string, value: number, min: number, max: number, step: number, onChange: (value: number) => void) => {
    return (
      <div className="flex flex-col items-center gap-2.5 w-full mb-4 p-3 bg-white/5 rounded-lg">
        <div className='flex flex-row items-center gap-3 w-full'>
          <span className="text-white/80 text-sm whitespace-nowrap">
            {`${title}:`}
          </span>
          <span className="text-white/60 text-sm">
            {desc}
          </span>
        </div>
        <div className='flex w-full items-center gap-2'>
          <button
            className="w-6 h-6 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
            onClick={() => {
              onChange(Math.max(min, value - step))
            }}
          >
            −
          </button>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={e => {
              onChange(Number(e.target.value))
            }}
            className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <button
            className="w-6 h-6 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
            onClick={() => {
              onChange(Math.min(max, value + step))
            }}
          >
            +
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className=" h-full w-full  bg-black flex items-center justify-center z-50"
      onClick={onCancel}
    >
      <div
        className="bg-gray-900 rounded-lg p-6 w-full max-w-[90vw] max-h-[90vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white text-xl font-medium">图片编辑器</h3>
          <div
            onClick={onCancel}
            className="text-white/60 hover:text-white text-2xl leading-none transition-colors"
          >
            ×
          </div>
        </div>

        {/* 工具栏 */}
        <div className="flex gap-2 mb-4 items-center justify-around">
          <Button
            size='sm'
            variant={mode === 'crop' ? 'primary' : 'white'}
            onClick={() => setMode(mode === 'crop' ? 'none' : 'crop')}
          >
            <svg className="w-10 h-5 " viewBox="0 0 54 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 2v13a.5.5 0 0 0 .5.5h13" />
              <path d="M15.5 20V7a.5.5 0 0 0-.5-.5H3" />
              <text x="25" y="16" font-size="14" fill="currentColor" stroke="none">裁剪</text>
            </svg>
          </Button>
          <Button
            size='sm'
            variant={mode === 'rotate' ? 'primary' : 'white'}
            onClick={() => setMode(mode === 'rotate' ? 'none' : 'rotate')}
          >
            <svg className="w-10 h-5 " viewBox="0 0 54 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12a7 7 0 0 1 12-5l2 2" />
              <path d="M19 4v5h-5" />
              <path d="M19 12a7 7 0 0 1-12 5l-2-2" />
              <path d="M5 20v-5h5" />
              <text x="26" y="16" font-size="14" fill="currentColor" stroke="none">旋转</text>
            </svg>
          </Button>
          <Button
            size='sm'
            variant='white'
            onClick={handleReset}>
            <svg className="w-10 h-5 " viewBox="0 0 54 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2.5 11a8.5 8.5 0 1 0 8.5-8.5 9 9 0 0 0-6.26 2.6L3 7" />
              <path d="M3 2.5v5h5" />
              <text x="25" y="16" font-size="14" fill="currentColor" stroke="none">重置</text>
            </svg>
          </Button>
        </div>

        {/* 旋转控制面板 */}
        {mode === 'rotate' && (
          _renderSlidView('角度', `${rotation}°`, rotation, -180, 180, 1, (value) => {
            setRotation(value)
          })
        )}

        {/* 裁剪控制面板 */}
        {mode === 'crop' && (
          _renderSlidView('缩放', `${zoom.toFixed(2)}x`, zoom, 0.8, 4, 0.1, (value) => {
            setZoom(value)
          })

        )}

        {/* 图片编辑区域 */}
        <div className="relative flex-1 min-h-87.5 bg-black/50 rounded-lg overflow-hidden">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            cropSize={{ width: 340 * aspect, height: 340 }}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={mode === 'crop'}
            style={{
              containerStyle: {
                backgroundColor: 'transparent',
              },
              cropAreaStyle: {
                border: '1px solid #3b82f6',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
              },
            }}
            onMediaLoaded={(mediaSize) => {
              console.log('onMediaLoaded >>> ', mediaSize, 290 / mediaSize.height)
              if (!editedInfo?.zoom) {
                let cropWidth = 340 * aspect
                let scaleW = cropWidth / mediaSize.width
                let scaleH = 340 / mediaSize.height
                initZoomRef.current = Math.max(scaleW, scaleH)
                setZoom(initZoomRef.current)
              }
            }}
          />
        </div>

        {/* 底部按钮 */}
        <div className="flex gap-3 mt-4 justify-end">
          <Button
            variant='white'
            className='min-w-10'
            onClick={onCancel}>
            取消
          </Button>
          <Button
            variant='primary'
            className=' min-w-10 '
            onClick={isProcessing ? () => { } : handleSave}
          >
            {isProcessing ? (
              <svg className='animate-spin h-4 w-4' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
              </svg>
            ) : (
              '确认'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
